'use client';

import { useCallback, useId, useMemo, useState } from 'react';
import { ActiveControl, useActiveControl } from '../useActiveControl';
import { useInvariantCallbackRef } from '../useInvariantCallbackRef';

import type { BlocksCloseEvent } from '@blocks-web-components/events';
import type {
  drawerProps,
  initiatorDrawerProps,
  useDrawerProps
} from './types';

export const useDrawer = ({
  id,
  onOpen,
  onClose,
  initialActive,

  // Some optional Drawer props
  position = 'right',
  size = 'default',
  returnFocusOnDeactivate = false,
  hasBackdrop = false,
  width
}: useDrawerProps = {}) => {
  const drawerControl: ActiveControl = useActiveControl({ initialActive });
  const { isActive, open, close } = drawerControl;

  const internalID = useId();
  const drawerId = id ?? internalID;

  // track if drawer is opened from another drawer
  const [isChild, setIsChild] = useState(false);

  /**
   * Controllers
   */
  const invariantOnOpenRef = useInvariantCallbackRef(onOpen);
  const openDrawer = useCallback(
    (_e = null, isChild = false) => {
      setIsChild(isChild);
      open();
      invariantOnOpenRef.current();
    },
    [open, invariantOnOpenRef]
  );

  const invariantOnCloseRef = useInvariantCallbackRef(onClose);
  const closeDrawer = useCallback(() => {
    close();
    invariantOnCloseRef.current();
    setIsChild(false);
  }, [close, invariantOnCloseRef]);

  /**
   * Initiator Props
   */
  const initiatorProps: initiatorDrawerProps = useMemo(
    () => ({
      'aria-controls': drawerId,
      'aria-expanded': isActive,
      onClick: openDrawer
    }),
    [drawerId, isActive, openDrawer]
  );

  /**
   * catDrawerProps
   */
  const drawerProps: drawerProps = useMemo(
    () => ({
      id: drawerId,
      isActive,
      onBlDrawerClose: (e: BlocksCloseEvent) => {
        e.stopPropagation();
        closeDrawer();
      },

      // Other optional props
      position,
      hasBackdrop,
      size,
      returnFocusOnDeactivate,
      width
    }),
    [
      drawerId,
      isActive,
      position,
      hasBackdrop,
      size,
      returnFocusOnDeactivate,
      width,
      closeDrawer
    ]
  );

  return useMemo(
    () => ({
      initiatorProps,
      drawerProps,

      isDrawerOpen: isActive,
      openDrawer,
      closeDrawer,
      isChild,
      // Deprecated props
      /**
       * This value will be removed soon, please use `isDrawerOpen`, `openDrawer` and `closeDrawer` values
       * @deprecated
       */
      drawerControl,
      /**
       * This value will be removed soon, please use `openDrawer` function
       * @deprecated
       */
      handleOpen: openDrawer,
      /**
       * This value will be removed soon, please use `closeDrawer` function
       * @deprecated
       */
      handleClose: closeDrawer
    }),
    [
      initiatorProps,
      drawerProps,
      isActive,
      openDrawer,
      closeDrawer,
      isChild,
      drawerControl
    ]
  );
};
